'use client';

import React, { useEffect } from 'react';
import type { AidaptivePageType, AidaptiveService } from '@/utils/aidaptive-analytics';
import { logAidaptiveSearchUser } from '@/utils/aidaptive-analytics';
import {
  AidaptiveTrackingData,
  buildTrackingRecord,
} from '@/utils/aidaptive-tracking';
import { gradientStyle } from '@/utils/gradient-style';
import { ImageSrcSetSize } from '@/utils/image';
import {
  Listing,
  ListingTracking,
} from '@/utils/platform-api/__generated__/graphql';
import dynamic from 'next/dynamic';
import { ClientBrandConfig } from 'types/brand-config';

import { CmsButton } from '@/components/ui/cms-button';

import type { ListingCarouselData } from './carousel';
import ListingCard from './listing-card';

const CmsCarousel = dynamic(() => import('@/components/ui/cms-carousel'));

const carouselCardImageSizes: ImageSrcSetSize[] = [
  { viewport: 320, image: 480 },
  { viewport: 1023, image: 640 },
  { viewport: 1600, image: 480 },
];

export type CarouselRendererProps = {
  brand: ClientBrandConfig;
  className?: string;
  data: ListingCarouselData;
  id?: string;
  listings: Listing[];
  tracking: ListingTracking[] | null;
  cohort: string | null;
  service: AidaptiveService;
  pageType: AidaptivePageType;
  usedAidaptiveSearch?: boolean;
};

export const CarouselRenderer: React.FC<CarouselRendererProps> = ({
  brand,
  className,
  data,
  id,
  listings,
  tracking,
  cohort,
  service,
  pageType,
  usedAidaptiveSearch = false,
}) => {
  const aidaptiveTracking: AidaptiveTrackingData = {
    record: buildTrackingRecord(tracking),
    customerCohort: cohort,
    service,
    pageType,
    usedAidaptiveSearch,
  };

  useEffect(() => {
    if (usedAidaptiveSearch) logAidaptiveSearchUser();
  }, [usedAidaptiveSearch]);

  const listingSlides = listings.map((listing, index) => {
    const t = aidaptiveTracking.record[listing.id];
    return {
      id: index,
      content: (
        <ListingCard
          id={listing.id}
          key={listing.id}
          listing={listing}
          className="flex h-full flex-col"
          imageSizes={carouselCardImageSizes}
          brand={brand}
          clickTrackerUrl={t?.clickUrl}
          impressionUrl={t?.impressionUrl}
          aidaptiveContext={aidaptiveTracking}
        />
      ),
    };
  });

  const bgGradient = gradientStyle(data?.backgroundGradient);
  if (listingSlides.length === 0) return null;

  return (
    <section
      className={`listing-carousel py-8 ${className ?? ''}`}
      id={id}
      style={bgGradient}
    >
      <div className="mx-auto max-w-screen-2xl">
        <div className="mx-auto mb-3 items-center justify-between px-4 text-center sm:mb-6 sm:flex sm:text-left">
          <h2 className="mb-3 text-primary-main sm:mb-0">{data.header}</h2>
          {data.links && <CmsButton key={0} link={data.links[0]} />}
        </div>
        <div className="sm:px-8">
          <CmsCarousel
            slides={listingSlides}
            options={{
              align: 'start',
              loop: false,
              containScroll: 'trimSnaps',
            }}
            slideContainerClassName="flex"
            slideWrapperClassName="flex-[0_0_80%] min-w-0 px-2.5 lg:px-4 py-3 sm:flex-[0_0_50%] xl:flex-[0_0_33.333%]"
          />
        </div>
      </div>
    </section>
  );
};
